import { MESSAGES_QUERY } from '@/data/gql/queries';
import { SUBMIT_SERVICE_MESSAGE, MARK_MESSAGE_READ, DELETE_MESSAGE } from '@/data/gql/mutations';

import Stores from '@/stores';
import apolloClient from '@/plugins/apollo';
import Message from '@/definitions/interfaces/Message.i';

class Messages {
  /**
   * fetch a user's messages
   * @return {Promise}
   */
  public async fetchMessages(): Promise<void> {
    Stores.message.loadingMessages = true;

    const _messages = await apolloClient.defaultClient.query({
      query: MESSAGES_QUERY,
      fetchPolicy: 'no-cache',
    });

    Stores.message.setMessages(_messages.data.profile.messages);
    Stores.message.loadingMessages = false;
  }

  /**
   * send a message to user
   * @param {string} userId - the UUID of the user to send the message to
   * @param {string} subject - the subject of the message
   * @param {string} content - the content of the message
   * @param {string|null} serviceId - the UUID of a service to tie the message to; leave blank if not applicable
   * @return {Promise} boolean
   */
  public async sendMessage(userId: string, subject: string, content: string, serviceId: string | null): Promise<boolean> {
    const _response = await apolloClient.defaultClient.mutate({
      mutation: SUBMIT_SERVICE_MESSAGE,
      variables: {
        userID: userId,
        subject: subject,
        content: content,
        serviceID: serviceId,
      },
    });
    return true;
  }

  /**
   * send a message to service chat, uses current profile user id
   * @param {string} serviceId
   * @param {string} subject
   * @param {string} content
   * @return {void}
   */
  public async sendServiceMessage(serviceId: string, subject: string, content: string): Promise<any> {
    return apolloClient.defaultClient.mutate({
      mutation: SUBMIT_SERVICE_MESSAGE,
      variables: {
        serviceID: serviceId,
        userID: Stores.profileData.profile.id,
        subject,
        content,
      },
    });
  }

  /**
   * mark a message read (both in the store and in the DB)
   * @param {string} messageIds - an array UUID's of the messages to mark as read
   * @return {Promise} boolean
   */
  public async markMessageRead(messageIds: string[]): Promise<boolean> {
    if (messageIds.length > 0) {
      const response = await apolloClient.defaultClient.mutate({
        mutation: MARK_MESSAGE_READ,
        variables: {
          messageIDs: messageIds,
        },
      });

      const currentDate = new Date();
      (Stores.message.messages as Message[]).forEach((message) => {
        if (messageIds.includes(message.id)) {
          message.confirmedOn = currentDate;
        }
      });

      return response.data.confirmMessageRead;
    } else {
      return false;
    }
  }

  /**
   * delete a message (both in the store and in the DB)
   * @param {string} messageIds - an array UUID's of messages to delete
   * @return {Promise} boolean
   */
  public async deleteMessage(messageIds: string[]): Promise<boolean> {
    if (messageIds.length > 0) {
      const response = await apolloClient.defaultClient.mutate({
        mutation: DELETE_MESSAGE,
        variables: {
          messageIDs: messageIds,
        },
      });

      Stores.message.messages = (Stores.message.messages as Message[]).filter((message) => !messageIds.includes(message.id));

      return response.data.deleteMessage;
    } else {
      return false;
    }
  }
}

export default new Messages();
